import EmptyState from '@/components/common/EmptyState';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useTheme } from '@/contexts/ThemeContext';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { Users } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Master {
    key: string;
    label: string;
}

interface Professor {
    id: number;
    name: string;
    bio: string | null;
    avatar_url: string | null;
    masters: Master[];
}

interface Props {
    professors: Professor[];
    masters: Master[];
    default_master: string | null;
}

interface MasterTabsProps {
    masters: Master[];
    activeMaster: string | null;
    onChange: (key: string | null) => void;
}

interface MasterTabProps {
    label: string;
    isActive: boolean;
    isFinance: boolean;
    gradientStyle: React.CSSProperties;
    primaryBg: string;
    onClick: () => void;
}

const MasterTab: React.FC<MasterTabProps> = ({
    label,
    isActive,
    isFinance,
    gradientStyle,
    primaryBg,
    onClick,
}) => {
    const [hovered, setHovered] = useState(false);
    const showGradient = isFinance && (isActive || hovered);
    const showPrimary = !isFinance && isActive;

    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`flex h-full w-full items-center justify-center rounded-xl px-3 py-2 text-center text-xs font-medium shadow-sm transition-all duration-150 focus:outline-none ${
                isFinance ? 'whitespace-normal' : 'whitespace-nowrap'
            } ${
                showGradient
                    ? 'text-black'
                    : showPrimary
                      ? `${primaryBg} text-white`
                      : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-md dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200'
            }`}
            style={showGradient ? gradientStyle : undefined}
        >
            {label}
        </button>
    );
};

const MasterTabs: React.FC<MasterTabsProps> = ({
    masters,
    activeMaster,
    onChange,
}) => {
    const colors = useThemeColors();
    const { theme } = useTheme();

    const gradientStyle = {
        background:
            'linear-gradient(to right, var(--color-theme-primary), var(--color-theme-secondary))',
    };

    return (
        <div className="mx-auto grid w-[90%] max-w-5xl grid-cols-1 gap-2 sm:w-full sm:grid-cols-4 sm:gap-2">
            {masters.map((master) => (
                <MasterTab
                    key={master.key}
                    label={master.label}
                    isActive={activeMaster === master.key}
                    isFinance={theme === 'cfinance'}
                    gradientStyle={gradientStyle}
                    primaryBg={colors.primaryBg}
                    onClick={() => onChange(master.key)}
                />
            ))}
        </div>
    );
};

const ProfessorsGalleryContent: React.FC<Props> = ({
    professors,
    masters = [],
    default_master,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { theme } = useTheme();
    const [activeMaster, setActiveMaster] = useState<string | null>(
        default_master ?? masters[0]?.key ?? null
    );

    const filtered =
        activeMaster === null
            ? professors
            : professors.filter((p) =>
                  p.masters.some((m) => m.key === activeMaster)
              );

    return (
        <AppLayout>
            <PageTitle title={t('professors.pageTitle')} />
            <div className="mx-auto max-w-6xl">
                {/* Page Header */}
                <div className="relative mb-10 text-center">
                    <div
                        className={`mx-auto mb-6 h-1 w-16 rounded-full bg-gradient-to-r ${colors.gradientFrom} ${colors.gradientTo}`}
                    />
                    <h1 className="mb-3 text-4xl font-bold text-gray-900 dark:text-white">
                        {t('professors.title')}
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-neutral-400">
                        {t('professors.description')}
                    </p>

                    {/* Master filter tabs — below description, wraps on small screens */}
                    {masters.length > 0 && (
                        <div className="mt-6">
                            <MasterTabs
                                masters={masters}
                                activeMaster={activeMaster}
                                onChange={setActiveMaster}
                            />
                        </div>
                    )}
                </div>

                {professors.length === 0 ? (
                    <EmptyState
                        icon={Users}
                        title={t('professors.noProfessorsTitle')}
                        description={t('professors.noProfessorsDescription')}
                    />
                ) : (
                    <>
                        {filtered.length === 0 ? (
                            <EmptyState
                                icon={Users}
                                description={t('professors.noResults')}
                            />
                        ) : (
                            <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                                {filtered.map((professor) => (
                                    <div
                                        key={professor.id}
                                        className="flex flex-col items-center text-center"
                                    >
                                        {/* Avatar — gradient background, PNG transparent shows through */}
                                        <div
                                            className="mb-5 h-48 w-48 overflow-hidden rounded-full"
                                            style={
                                                theme === 'cfinance'
                                                    ? {
                                                          backgroundImage:
                                                              'linear-gradient(225deg, var(--color-theme-secondary), var(--color-theme-primary))',
                                                      }
                                                    : {
                                                          backgroundImage:
                                                              'linear-gradient(225deg, #ff4000, #ffbf00)',
                                                      }
                                            }
                                        >
                                            <img
                                                src={professor.avatar_url || ''}
                                                alt={professor.name}
                                                loading="lazy"
                                                className="h-full w-full object-cover"
                                            />
                                        </div>

                                        {/* Name */}
                                        <h3 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
                                            {professor.name}
                                        </h3>

                                        {/* Bio */}
                                        {professor.bio && (
                                            <p className="text-base leading-relaxed text-gray-600 dark:text-neutral-300">
                                                {professor.bio}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </AppLayout>
    );
};

const ProfessorsGallery: React.FC<Props> = ({
    professors,
    masters,
    default_master,
}) => (
    <ThemeWrapper>
        <ProfessorsGalleryContent
            professors={professors}
            masters={masters}
            default_master={default_master}
        />
    </ThemeWrapper>
);

export default ProfessorsGallery;
